import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { fetchTimbradoDe, saveTimbradoPagina, type AlvoDoTimbrado } from '@/api/timbrado';
import {
  DOCUMENTO_PADRAO,
  MARGENS_PRONTAS,
  PAPEIS,
  paginaDoDocumento,
  type Margens,
  type Orientacao,
  type PaginaPadrao,
  type Papel,
} from '@/components/editor-forense';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { chaveDoTimbrado } from '@/lib/editor/timbradoEditor';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

const DO_APP: PaginaPadrao = paginaDoDocumento(DOCUMENTO_PADRAO);
const PERSONALIZADAS = 'personalizadas';
const LADOS: { chave: keyof Margens; rotulo: string }[] = [
  { chave: 'superior', rotulo: 'Superior' },
  { chave: 'inferior', rotulo: 'Inferior' },
  { chave: 'esquerda', rotulo: 'Esquerda' },
  { chave: 'direita', rotulo: 'Direita' },
];

const fmt = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
export const resumoDaPagina = (p: PaginaPadrao) =>
  `${PAPEIS[p.papel].nome}, ${p.orientacao}, margens ${p.nomeMargens === PERSONALIZADAS ? LADOS.map((l) => fmt(p.margens[l.chave])).join(' / ') + ' cm' : p.nomeMargens}`;

/**
 * A página com que as peças novas abrem (papel, orientação, margens) — a pessoal
 * (Configurações → Papel timbrado) ou a de um escritório (a aba Peças dele). Mora com
 * o timbrado (o mesmo `timbrado_config`); sem nenhuma guardada, vale a do app (A4, ABNT).
 */
export function EditorDePagina({ alvo, podeEditar = true }: { alvo: AlvoDoTimbrado; podeEditar?: boolean }) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const chave = chaveDoTimbrado(alvo);
  const { data, isFetching } = useQuery({ queryKey: chave, queryFn: () => fetchTimbradoDe(alvo), refetchOnMount: 'always' });
  const guardada = data?.pagina ?? null;
  const [p, setP] = useState<PaginaPadrao | null>(null);
  // Hidrata uma vez, quando chega (o cache que muda depois não reescreve o que se edita).
  if (data && !p && !isFetching) setP(guardada ?? DO_APP);

  const gravar = useMutation({
    mutationFn: (nova: PaginaPadrao | null) => saveTimbradoPagina(nova, alvo),
    onSuccess: async (salvo) => {
      queryClient.setQueryData(chave, salvo);
      setP(salvo.pagina ?? DO_APP);
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.timbrado.resolvido] });
      showSuccess(salvo.pagina ? 'Página salva: as próximas peças abrem com ela.' : 'As próximas peças abrem na página do app.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  if (!p) {
    return (
      <div className="flex justify-center py-4">
        <Spinner size={32} label="Carregando a página" />
      </div>
    );
  }
  if (!podeEditar) {
    return <p className="text-sm text-muted-foreground">{guardada ? resumoDaPagina(guardada) : 'A do app: A4, retrato, margens ABNT.'}</p>;
  }
  const pronta = MARGENS_PRONTAS.find((m) => m.nome === p.nomeMargens);
  const id = (c: string) => `pagina-${alvo === 'me' ? 'me' : alvo.escritorio}-${c}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="flex flex-col gap-2">
          <Label htmlFor={id('papel')}>Papel</Label>
          <Select value={p.papel} onValueChange={(v) => setP({ ...p, papel: v as Papel })}>
            <SelectTrigger id={id('papel')}><SelectValue /></SelectTrigger>
            <SelectContent>
              {(Object.keys(PAPEIS) as Papel[]).map((k) => (
                <SelectItem key={k} value={k}>{PAPEIS[k].nome} ({fmt(PAPEIS[k].largura)} × {fmt(PAPEIS[k].altura)} cm)</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor={id('orientacao')}>Orientação</Label>
          <Select value={p.orientacao} onValueChange={(v) => setP({ ...p, orientacao: v as Orientacao })}>
            <SelectTrigger id={id('orientacao')}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="retrato">Retrato</SelectItem>
              <SelectItem value="paisagem">Paisagem</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor={id('margens')}>Margens</Label>
          <Select
            value={pronta ? pronta.nome : PERSONALIZADAS}
            onValueChange={(v) => {
              const m = MARGENS_PRONTAS.find((x) => x.nome === v);
              setP(m ? { ...p, margens: { ...m.margens }, nomeMargens: m.nome } : { ...p, nomeMargens: PERSONALIZADAS });
            }}
          >
            <SelectTrigger id={id('margens')}><SelectValue /></SelectTrigger>
            <SelectContent>
              {MARGENS_PRONTAS.map((m) => (
                <SelectItem key={m.chave} value={m.nome}>{m.nome} ({m.resumo})</SelectItem>
              ))}
              <SelectItem value={PERSONALIZADAS}>Personalizadas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {!pronta && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {LADOS.map((l) => (
            <div key={l.chave} className="flex flex-col gap-2">
              <Label htmlFor={id(l.chave)}>{l.rotulo} (cm)</Label>
              <Input
                id={id(l.chave)}
                type="number"
                inputMode="decimal"
                min={0.5}
                max={6}
                step={0.25}
                value={p.margens[l.chave]}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (Number.isFinite(v)) setP({ ...p, margens: { ...p.margens, [l.chave]: Math.min(6, Math.max(0, v)) } });
                }}
              />
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        {guardada && (
          <Button variant="outline" onClick={() => gravar.mutate(null)} disabled={gravar.isPending}>
            Voltar à página do app
          </Button>
        )}
        <Button onClick={() => gravar.mutate(p)} disabled={gravar.isPending}>
          {gravar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
          Salvar página
        </Button>
      </div>
    </div>
  );
}
